import { useCallback, useState } from 'react';
import { templateService } from '../services/templateService';

export function sanitizeWabaNumber(num) {
  if (!num) return '';
  return String(num).replace(/[^0-9]/g, '');
}

export function normalizePhoneHealth(raw) {
  if (!raw) return null;
  const item = raw?.data?.data ?? raw?.data ?? raw;
  if (!item || typeof item !== 'object' || Object.keys(item).length === 0) return null;

  return {
    id: item.id || '',
    phoneNumber: item.display_phone_number || item.phone_number || item.waba_number || item.wa_phone_number || '',
    displayPhoneNumber: item.display_phone_number || item.phone_number || item.waba_number || '',
    accountMode: String(item.account_mode || 'LIVE').toUpperCase(),
    qualityRating: String(
      item.quality_rating ||
      item.quality_score?.score ||
      item.quality_score ||
      item.quality ||
      'UNKNOWN'
    ).toUpperCase(),
    status: String(item.status || item.health_status || 'UNKNOWN').toUpperCase(),
    nameStatus: String(item.name_status || '—').toUpperCase(),
    messagingLimitTier: item.messaging_limit_tier || item.current_limit || item.tier || item.limit || '—',
    verifiedName: item.verified_name || item.name || item.requested_verified_name || item.display_name || '—',
    codeVerificationStatus: item.code_verification_status || item.verification_status || '—',
    wabaId: item.waba_id || '',
    raw: item,
  };
}

export function normalizeAllPhoneHealth(raw) {
  if (!raw) return [];
  const root = raw?.results ?? raw?.data?.data ?? raw?.data?.results ?? raw?.data ?? raw;
  if (!Array.isArray(root)) {
    if (typeof root === 'object' && root !== null) {
      const normalizedSingle = normalizePhoneHealth(root);
      return normalizedSingle ? [normalizedSingle] : [];
    }
    return [];
  }

  const flattened = [];
  root.forEach((entry) => {
    if (Array.isArray(entry?.data)) {
      entry.data.forEach((subItem) => {
        flattened.push({ ...subItem, waba_id: entry.waba_id || subItem.waba_id });
      });
    } else if (entry && typeof entry === 'object') {
      flattened.push(entry);
    }
  });

  return flattened.map(normalizePhoneHealth).filter(Boolean);
}

export function normalizeTemplateHealth(raw) {
  if (!raw) return null;
  const item = raw?.data?.data ?? raw?.data ?? raw;
  if (!item || typeof item !== 'object' || Object.keys(item).length === 0) return null;

  const rawQualityScore = item.quality_score;
  const scoreVal = typeof rawQualityScore === 'object' ? rawQualityScore?.score : rawQualityScore;
  const scoreDate = typeof rawQualityScore === 'object' ? rawQualityScore?.date : null;
  const rejection = item.rejected_reason || item.rejection_reason || item.reason || item.failed_reason || '';

  return {
    id: item.id || '',
    name: item.name || item.template_name || '',
    status: String(item.status || item.template_status || 'UNKNOWN').toUpperCase(),
    qualityScore: String(scoreVal || item.quality_rating || item.quality || 'UNKNOWN').toUpperCase(),
    qualityScoreDate: scoreDate,
    category: item.category || '—',
    language: item.language || 'en',
    rejectionReason: rejection && rejection !== 'NONE' ? rejection : '',
    wabaNumber: item.waba_number || item.bot_number || '',
    raw: item,
  };
}

export default function useHealth() {
  const [phoneHealth, setPhoneHealth] = useState({
    data: null,
    raw: null,
    loading: false,
    error: null,
    checkedNumber: null,
  });

  const [allPhoneHealth, setAllPhoneHealth] = useState({
    data: [],
    raw: null,
    loading: false,
    error: null,
  });

  const [templateHealth, setTemplateHealth] = useState({
    data: null,
    raw: null,
    loading: false,
    error: null,
    checkedTemplate: null,
  });

  const [wabaMsgLimit, setWabaMsgLimit] = useState({
    data: null,
    raw: null,
    loading: false,
    error: null,
  });

  const [callbackState, setCallbackState] = useState({
    data: null,
    loading: false,
    error: null,
    success: false,
  });

  const fetchPhoneHealth = useCallback(async (wabaNumber, companyId) => {
    const cleanNumber = sanitizeWabaNumber(wabaNumber);
    if (!cleanNumber) {
      setPhoneHealth((prev) => ({
        ...prev,
        error: 'WABA number is required',
        loading: false,
      }));
      return null;
    }

    setPhoneHealth((prev) => ({
      ...prev,
      loading: true,
      error: null,
      checkedNumber: cleanNumber,
    }));

    try {
      const res = await templateService.getTrustSignalPhoneHealth(cleanNumber, companyId);
      const resData = res?.data ?? res;
      const normalized = normalizePhoneHealth(resData);
      setPhoneHealth({
        data: normalized,
        raw: resData,
        loading: false,
        error: null,
        checkedNumber: cleanNumber,
      });
      return normalized;
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to fetch phone health';
      setPhoneHealth((prev) => ({
        ...prev,
        data: null,
        raw: err?.response?.data ?? null,
        loading: false,
        error: errMsg,
        checkedNumber: cleanNumber,
      }));
      return null;
    }
  }, []);

  const fetchAllPhoneHealth = useCallback(async (wabaIdOrParams, companyId) => {
    let targetWabaId = wabaIdOrParams;
    let targetCompanyId = companyId;
    if (typeof wabaIdOrParams === 'object' && wabaIdOrParams !== null) {
      targetWabaId = wabaIdOrParams.waba_id || wabaIdOrParams.wabaId;
      targetCompanyId = wabaIdOrParams.company_id || wabaIdOrParams.companyId || companyId;
    }

    if (!targetWabaId) {
      setAllPhoneHealth((prev) => ({
        ...prev,
        loading: false,
        error: 'WABA ID is required to fetch all phone numbers under a WABA.',
      }));
      return [];
    }
    setAllPhoneHealth((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const res = await templateService.getTrustSignalAllPhoneHealth(targetWabaId, targetCompanyId);
      const resData = res?.data ?? res;
      const normalizedList = normalizeAllPhoneHealth(resData);
      setAllPhoneHealth({
        data: normalizedList,
        raw: resData,
        loading: false,
        error: null,
      });
      return normalizedList;
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to fetch all phone health';
      setAllPhoneHealth({
        data: [],
        raw: err?.response?.data ?? null,
        loading: false,
        error: errMsg,
      });
      return [];
    }
  }, []);

  const fetchTemplateHealth = useCallback(async ({ name, wabaNumber, companyId, templateId } = {}) => {
    if (!name && !templateId) {
      setTemplateHealth((prev) => ({
        ...prev,
        error: 'Template name or ID is required',
        loading: false,
      }));
      return null;
    }

    const cleanNumber = sanitizeWabaNumber(wabaNumber);

    setTemplateHealth((prev) => ({
      ...prev,
      loading: true,
      error: null,
      checkedTemplate: name || templateId,
    }));

    try {
      const effectiveId = templateId || name;
      const res = await templateService.getTrustSignalTemplateHealth({
        name,
        wabaNumber: cleanNumber,
        companyId,
        templateId: effectiveId,
      });
      const resData = res?.data ?? res;
      const normalized = normalizeTemplateHealth(resData);
      setTemplateHealth({
        data: normalized,
        raw: resData,
        loading: false,
        error: null,
        checkedTemplate: name || templateId,
      });
      return normalized;
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to fetch template health';
      setTemplateHealth((prev) => ({
        ...prev,
        data: null,
        raw: err?.response?.data ?? null,
        loading: false,
        error: errMsg,
        checkedTemplate: name || templateId,
      }));
      return null;
    }
  }, []);

  const triggerCallback = useCallback(async (payload = {}) => {
    setCallbackState({
      data: null,
      loading: true,
      error: null,
      success: false,
    });

    try {
      const res = await templateService.triggerTrustSignalCallback(payload);
      const resData = res?.data ?? res;
      setCallbackState({
        data: resData,
        loading: false,
        error: null,
        success: true,
      });
      return { success: true, data: resData };
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to trigger TrustSignal callback';
      setCallbackState({
        data: err?.response?.data ?? null,
        loading: false,
        error: errMsg,
        success: false,
      });
      return { success: false, error: errMsg };
    }
  }, []);

  const fetchWabaMsgLimit = useCallback(async (wabaId) => {
    const cleanId = String(wabaId || '').trim();
    if (!cleanId) {
      return null;
    }

    setWabaMsgLimit((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const res = await templateService.getTrustSignalWabaMsgLimit(cleanId);
      const resData = res?.data ?? res;
      const records = resData?.waba_records || [];
      const record = records[0] || null;
      const messagingLimit = record?.messaging_limit || record?.tier || null;

      const normalized = {
        messagingLimit: messagingLimit || '—',
        status: record?.status || '',
        name: record?.name || '',
        wabaId: record?.waba_id || cleanId,
        raw: resData,
      };

      setWabaMsgLimit({
        data: normalized,
        raw: resData,
        loading: false,
        error: null,
      });

      // Also enrich phoneHealth with messaging limit tier if already loaded
      setPhoneHealth((prev) => {
        if (!prev.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            messagingLimitTier: messagingLimit || prev.data.messagingLimitTier,
          },
        };
      });

      return normalized;
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to fetch messaging limit';
      setWabaMsgLimit((prev) => ({
        ...prev,
        loading: false,
        error: errMsg,
      }));
      return null;
    }
  }, []);

  const resetAll = useCallback(() => {
    setPhoneHealth({ data: null, raw: null, loading: false, error: null, checkedNumber: null });
    setAllPhoneHealth({ data: [], raw: null, loading: false, error: null });
    setTemplateHealth({ data: null, raw: null, loading: false, error: null, checkedTemplate: null });
    setCallbackState({ data: null, loading: false, error: null, success: false });
    setWabaMsgLimit({ data: null, raw: null, loading: false, error: null });
  }, []);

  return {
    phoneHealth,
    allPhoneHealth,
    templateHealth,
    callbackState,
    fetchPhoneHealth,
    fetchAllPhoneHealth,
    fetchTemplateHealth,
    wabaMsgLimit,
    fetchWabaMsgLimit,
    triggerCallback,
    resetAll,
  };
}
